import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { 
	withStyles,
	Button,
	Grid,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Link,
	Typography
} from '@material-ui/core';
import { addOfficeId, clearConnectMlsState, sendMlsReviewRequest } from 'redux/modules/mls';
import classNames from 'classnames';
import ValidationSchema from './AddMlsOfficeModalSchema';
import { openIntercomChat } from 'utils/intercom';
import { hideModal, resetModal } from 'redux/modules/modal';
import { showErrorPopup } from 'redux/modules/companies';
import PropTypes from 'prop-types';
import FormField from '../FormField';
import { XIcon, SpinnerIcon, IconTickRounded, IconErrorRounded, IconInfoRounded } from '../../theme/icons';
import { sendVerifyAccountEmail } from 'redux/modules/account';
import _ from 'lodash';
import ERRORS from '../../errors/errorids';
import { 
	trackAddMlsOfficePopupShown,
	trackAddMlsOfficeSuccess,
	trackAddMlsOfficeFailure,
	trackSentReviewMlsToXaraFailure,
	trackSentReviewMlsToXaraSuccess
} from 'lib/Segment/customTrackers';
import { getTrackingOptions } from 'lib/Segment/helpers';
import { getMlsFormProps } from 'utils/forms';
import { MLS_ERRORS } from 'utils/mls';

import i18n from 'i18n/i18n';
const localize = i18n.loc.localize;

const styles = theme => {
	return {
		...getMlsFormProps(theme),

		/* Dialog */
		dialogPaper: {
			minWidth: 444
		},
		dialogWrapper: {
			position: 'relative',
			padding: '15px 10px 0',
			minHeight: 400,
			display: 'flex',
			flexDirection: 'column'
		},

		/* loading icon */
		loadingContainer: {
			minHeight: 400,
			position: 'relative'
		},
	
		introMessage: {
			marginBottom: 40
		}
	};
};

const SCREEN_TYPE = {
	MLS_API: 'MLS_API',
	MLS_OFFICE_ID_REVIEW_NEEDED: 'MLS_OFFICE_ID_REVIEW_NEEDED'
};

@withStyles(styles)
class InviteUserModal extends Component {

	constructor(props) {
		super(props);

		this.state = {
			currentStep: SCREEN_TYPE.MLS_API,
			mlsOfficeId: '',
			xhrInProgress: false,
			apiRequestSuccess: false,
			apiRequestError: false,
			errors: {},
			touched: {}
		};

		this.handleOpenIntercom = this.handleOpenIntercom.bind(this);
	}

	render() {
		const { modalShown, resetModal, classes } = this.props;

		return (
			<Dialog
				open={modalShown}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				onExited={resetModal}
				BackdropProps={{ style: { backgroundColor: 'rgba(46, 63, 79, 0.4)' } }}
				classes={{ paper: classes.dialogPaper }}
				PaperProps={{
					style: {
						border: 0,
						width: 309,
						minWidth: 505,
						borderRadius: 5,
						boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.08)'
					}
				}}
			>
				<div className={classes.dialogWrapper}>
					{!this.state.xhrInProgress && (
						<IconButton
							className={classes.closeDialog}
							component='span'
							aria-label='Close'
							onClick={() => this.closeDialog()}
						>
							<XIcon className={classes.closeDialogIcon} />
						</IconButton>
					)}
					
					{(() => {
						if (this.state.xhrInProgress) {
							return this.renderLoading();
						} else if (this.state.currentStep === SCREEN_TYPE.MLS_OFFICE_ID_REVIEW_NEEDED) {

							return this.props.mls.reviewMlsSuccess
								?
								this.renderSuccess()
								:
								this.renderReviewMlsId();

						} else if (this.state.apiRequestSuccess) {
							return this.renderSuccess();
						} else if (this.state.apiRequestError) {
							return this.renderFailure();
						} else {
							return this.renderForm();
						}
					})()}
				</div>
			</Dialog>
		);
	}

	closeDialog() {
		const shouldReloadList = this.state.apiRequestSuccess && this.state.currentStep === SCREEN_TYPE.MLS_API;
		this.props.onClose(shouldReloadList);
	}

	backToForm() {
		this.props.clearConnectMlsState();
		this.setState({
			currentStep: SCREEN_TYPE.MLS_API,
			xhrInProgress: false,
			apiRequestSuccess: false,
			apiRequestError: false,
			errors: {}
		});
	}

	renderLoading() {
		const { classes } = this.props;
		return (
			<div className={classes.loadingContainer}>
				<div className={classes.loadingContent}>
					<SpinnerIcon className={classes.loadingIcon} />
					<Typography className={classes.loadingMessage} variant="body2">
						{localize('mls-popup-saving-form')}
					</Typography>
				</div>
			</div>
		);
	}

	renderSuccess() {
		const { classes } = this.props;
		return (
			<Fragment>
				<DialogTitle
					variant='body1'
					color='default'
					className={classes.dialogTitle}>
					<IconTickRounded className={classes.headerIcon} /> {
						localize(
							this.state.currentStep === SCREEN_TYPE.MLS_OFFICE_ID_REVIEW_NEEDED
								? 'mls-request-success-screen-title' : 'mls-office-success-screen-title')
					}
				</DialogTitle>
				<DialogContent className={classes.dialogContent} >
					<Grid container direction="column">

						<Grid item className={classes.formMessage}>
							<Typography
								variant="body2"
								dangerouslySetInnerHTML={{ __html: 
									this.state.currentStep === SCREEN_TYPE.MLS_OFFICE_ID_REVIEW_NEEDED
										?
										localize('mls-request-success-screen-description')
										:
										localize(
											'mls-office-success-screen-description',
											[this.state.mlsOfficeId, this.props.mlsName]
										)
								}} />
						</Grid>

					</Grid>
				</DialogContent>
				<DialogActions>
					<Grid
						item
						container
						direction="row"
						justify="flex-end"
						className={classes.buttonsContainer}>

						{this.state.currentStep === SCREEN_TYPE.MLS_API && (
							<Button
								variant='contained'
								color='primary'
								data-tid="button-close-dialog"
								className={classNames(classes.button, classes.buttonPrimary)}
								onClick={() => this.closeDialog()}
							>
								{localize('mls-request-success-screen-close')}
							</Button>
						)}

						{this.state.currentStep === SCREEN_TYPE.MLS_OFFICE_ID_REVIEW_NEEDED && (
							<Button
								variant='contained'
								color='primary'
								data-tid="button-close-dialog"
								className={classNames(classes.button, classes.buttonPrimary)}
								onClick={() => this.closeDialog()}
							>
								{localize('mls-request-success-screen-got-it')}
							</Button>							
						)}

					</Grid>
				</DialogActions>
			</Fragment>
		);
	}

	renderFailure() {
		const { classes } = this.props;

		const errors = this.state.errors;
		const apiError = _.get(errors, 'apiError');

		let headerIcon;
		let popupTitle;
		let showSendToReview = false;

		if (this.props.mls.addMlsOfficeIdErrorId &&
			this.props.mls.addMlsOfficeIdErrorId === MLS_ERRORS.NO_LISTINGS_FOUND) {
			popupTitle = localize('mls-error-connection-screen-title-no-listings-found');
			headerIcon = <IconInfoRounded className={classNames(classes.headerIcon, classes.headerIconInfo)} />;
			showSendToReview = true;
		} else {
			popupTitle = localize('mls-error-office-screen-title');
			headerIcon = <IconErrorRounded className={classes.headerIcon} />;
		}

		return (
			<Fragment>
				<DialogTitle
					variant='body1'
					color='default'
					className={classes.dialogTitle}>
					{headerIcon} {popupTitle}
				</DialogTitle>
				<DialogContent className={classes.dialogContent} >
					<Grid container direction="column">
						<Grid item className={classes.formMessage}>
							<Typography
								variant="body2"
								dangerouslySetInnerHTML={{ __html: apiError }} />

							{this.renderContactForHelp()}
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Grid
						item
						container
						direction="row"
						justify="flex-end"
						className={classes.buttonsContainer}>

						<Button
							variant='outlined'
							color='primary'
							data-tid="button-back"
							className={classNames(classes.button, classes.buttonBack)}
							onClick={() => this.backToForm()}
						>
							{localize('mls-error-screen-back')}
						</Button>

						{
							showSendToReview
								?
								(
									<Button
										variant='contained'
										color='primary'
										data-tid="button-send-to-review-office-id"
										className={classNames(classes.button, classes.buttonPrimary)}
										onClick={() => this.sendToReview()}
									>
										{localize('mls-error-screen-send-to-review')}
									</Button>
								)
								:
								(
									<Button
										variant='contained'
										color='primary'
										data-tid="button-close-dialog"
										className={classNames(classes.button, classes.buttonPrimary)}
										onClick={() => this.closeDialog()}
									>
										{localize('mls-error-screen-close')}
									</Button>
								)
						}						
					</Grid>
				</DialogActions>
			</Fragment>
		);		
	}	

	renderReviewMlsId() {
		const { classes } = this.props;
		const headerIcon = <IconInfoRounded className={classNames(classes.headerIcon, classes.headerIconInfo)} />;
		const popupTitle = localize('mls-error-connection-screen-title-no-listings-found');
		const errorMessage = localize('mls-popup-no-listings-found-for-office-id', [this.props.mlsName, this.state.mlsOfficeId]);

		return (
			<Fragment>
				<DialogTitle
					variant='body1'
					color='default'
					className={classes.dialogTitle}>
					{headerIcon} {popupTitle}
				</DialogTitle>
				<DialogContent className={classes.dialogContent} >
					<Grid container direction="column">
						{Object.keys(this.state.errors).length > 0 && this.renderErrorMessage()}

						<Grid item className={classNames(classes.failureMessage, classes.feedbackMessage)}>
							<Typography
								variant="body2"
								dangerouslySetInnerHTML={{ __html: errorMessage }} />

							{_.get(this.props.mls, 'reviewMlsErrorId') && this.renderContactForHelp()}
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Grid
						item
						container
						direction="row"
						justify="flex-end"
						className={classes.buttonsContainer}>

						<Button
							variant='outlined'
							color='primary'
							data-tid="button-back-to-form"
							className={classNames(classes.button, classes.buttonBack)}
							onClick={() => this.backToForm()}
						>
							{localize('mls-error-screen-back')}
						</Button>
						<Button
							variant='contained'
							color='primary'
							data-tid="button-send-to-review-office-id"
							className={classNames(classes.button, classes.buttonPrimary)}
							onClick={() => this.sendToReview()}
						>
							{localize('mls-error-screen-send-to-review')}
						</Button>
					</Grid>
				</DialogActions>
			</Fragment>
		);		
	}	

	renderErrorMessage() {
		const errors = this.state.errors;
		const apiError = _.get(errors, 'apiError');
		const formError = this.hasFormError(errors) && 'mls-popup-errors-found-in-the-form';
		
		let errorMessageKey;
		if (formError) {
			errorMessageKey = formError;
		} else if (apiError) {
			errorMessageKey = apiError;
		}
		const { classes } = this.props;
		return (
			<Typography
				className={classes.errorMessage}
				variant="body2"
				dangerouslySetInnerHTML={{ __html: localize(errorMessageKey) }}
			/>
		);		
	}

	renderContactForHelp() {
		return (
			<Typography variant="body2">
				<Link onClick={this.handleOpenIntercom}>
					{localize('mls-error-screen-contact-for-help-1')}
				</Link>&nbsp;
				{localize('mls-error-screen-contact-for-help-2')}
			</Typography>		
		);
	}

	handleOpenIntercom() {
		openIntercomChat(null, true, this.props.showErrorPopup);
	}

	renderForm() {
		const { classes } = this.props;
		const { mlsOfficeId } = this.state;

		return (
			<Fragment>
				<DialogTitle
					variant='body1'
					color='default'
					className={classes.dialogTitle}>
					{localize('mls-office-popup-header')}
				</DialogTitle>
				<DialogContent className={classes.dialogContent} >
					<Grid container direction="column">

						<Grid item className={classNames(classes.formMessage, classes.introMessage)}>
							<Typography
								variant="body2"
								dangerouslySetInnerHTML={{
									__html: localize('mls-office-popup-description', this.props.mlsName)
								}} />
						</Grid>

						{Object.keys(this.state.errors).length > 0 && this.renderErrorMessage()}
						<Fragment>
							<Grid item className={classes.textFieldWrapper}>
								{this.textField(
										'mlsOfficeId',
										mlsOfficeId,
										localize('mls-office-id-placeholder'),
										null,
										null,
										null,
										false
								)}
							</Grid>
						</Fragment>

						<Grid item className={classes.linksContainer}>
							<Typography variant="body1" className={classes.link}>
								<Link onClick={this.handleOpenIntercom}>
									{localize('mls-popup-contact-us')}
								</Link>
							</Typography>
						</Grid>

					</Grid>
				</DialogContent>
				<DialogActions>
					<Grid item container direction="row" justify="flex-end" className={classes.buttonsContainer}>
						<Button
							variant='contained'
							color='primary'
							data-tid="button-connect-mls"
							className={classNames(classes.button, classes.buttonPrimary)}
							onClick={() => this.submitForm()}
							disabled={Object.keys(this.getFormErrors(true)).length > 0}>
							{localize(
								this.state.xhrInProgress
									?
									'mls-popup-sending-request'
									:
									'mls-popup-connect')
							}
						</Button>
					</Grid>
				</DialogActions>
			</Fragment>
		);
	}

	async submitForm(forceAddition = false) {
		this.setState({ 
			xhrInProgress: true,
			apiRequestError: false,
			apiRequestSuccess: false,
			errors: { apiError: null }
		});

		this.props.addOfficeId(
			this.props.mlsConnectionId,
			this.state.mlsOfficeId,
			forceAddition
		);
	}

	async sendToReview() {
		this.setState({ 
			xhrInProgress: true,
			apiRequestError: false,
			apiRequestSuccess: false,
			errors: { apiError: null }
		});

		this.props.sendMlsReviewRequest(
			{
				'user-email': _.get(this.props.user, 'mainEmail'),
				'user-xc-id': _.get(this.props.user, 'userId'),
				'mls-system': this.props.mlsName,
				'mls-agent-id': '-',
				'mls-office-id': this.state.mlsOfficeId,
				'process-review': 1
			}
		);
	}	

	isSubmitEnabled() {
		return !this.hasFormError(this.state.errors) ||
		Object.keys(this.state.errors).length === 1 && _.get(this.state, 'errors.apiError');
	}

	hasFormError(errorState) {
		let result = false;

		Object.keys(errorState).forEach(fieldName => {
			if (fieldName.indexOf('mlsOfficeId') !== -1) {
				result = true;
			}
		});

		return result;
	}

	componentDidMount() {
		trackAddMlsOfficePopupShown(getTrackingOptions(this.props.company));
	}

	componentWillReceiveProps(nextProps) {
		if (
			(
				this.props.mls.reviewMlsErrorId !== nextProps.mls.reviewMlsErrorId &&
				nextProps.mls.reviewMlsErrorId !== null				
			) ||
			(this.props.mls.addMlsOfficeIdErrorId !== nextProps.mls.addMlsOfficeIdErrorId &&
				nextProps.mls.addMlsOfficeIdErrorId !== null)
		) {
			this.handleApiErrors(nextProps);

			if (nextProps.mls.reviewMlsErrorId) {
				trackSentReviewMlsToXaraFailure(getTrackingOptions(this.props.company));
			} else if (nextProps.mls.addMlsOfficeIdErrorId) {
				trackAddMlsOfficeFailure(getTrackingOptions(this.props.company));
			}
			
		}

		if (this.props.mls.addMlsOfficeIdErrorId !== nextProps.mls.addMlsOfficeIdErrorId &&
			nextProps.mls.addMlsOfficeIdErrorId === ERRORS.EMAIL_NOT_VERIFIED
		) {
			this.props.sendVerifyAccountEmail(_.get(this.props.user, 'mainEmail'));
		}		

		if (
			(this.props.mls.reviewMlsSuccess !== nextProps.mls.reviewMlsSuccess && nextProps.mls.reviewMlsSuccess === true) || 
			(this.props.mls.addMlsOfficeIdSuccess !== nextProps.mls.addMlsOfficeIdSuccess &&
				nextProps.mls.addMlsOfficeIdSuccess === true)
		) {
			if (nextProps.mls.addMlsOfficeIdSuccess) {
				trackAddMlsOfficeSuccess(getTrackingOptions(this.props.company));
			} else if (nextProps.mls.reviewMlsSuccess) {
				trackSentReviewMlsToXaraSuccess(getTrackingOptions(this.props.company));
			}

			this.setState({
				xhrInProgress: false,
				apiRequestError: false,
				apiRequestSuccess: true,
				errors: { apiError: null }
			});			
		}	
		

		
	}

	handleApiErrors(nextProps) {
		let errorMessageKey;
		let errorMessage;

		if (nextProps.mls.reviewMlsErrorId) {
			errorMessageKey = nextProps.mls.reviewMlsErrorId &&
				MLS_ERRORS[nextProps.mls.reviewMlsErrorId] || 
				MLS_ERRORS[ERRORS.UNKNOWN];

		} else if (nextProps.mls.addMlsOfficeIdErrorId) {
			errorMessageKey = nextProps.mls.addMlsOfficeIdErrorId &&
				MLS_ERRORS[nextProps.mls.addMlsOfficeIdErrorId] || 
				MLS_ERRORS[ERRORS.UNKNOWN];
		}

		if (this.state.currentStep === SCREEN_TYPE.MLS_API &&
			nextProps.mls.addMlsOfficeIdErrorId === ERRORS.MLS_NO_LISTINGS_FOUND) {

			this.setState({
				xhrInProgress: false,
				apiRequestError: false,
				apiRequestSuccess: false,
				currentStep: SCREEN_TYPE.MLS_OFFICE_ID_REVIEW_NEEDED,
				errors: {}
			});
		} else {
			errorMessage = localize(errorMessageKey);

			this.setState({
				xhrInProgress: false,
				apiRequestError: true,
				apiRequestSuccess: false,
				errors: { apiError: errorMessage }
			});
		}
	}		

	textField(name, value, placeholder, disabled, type, maxLength, autosize) {
		const { classes } = this.props;
		
		return (
			<FormField
				value={value}
				autoComplete='off'
				className={classes.field}
				data-tid={`form-user-${name}`}
				onChange={event => this.onTextFieldChange(event, name)}
				onBlur={event => this.onTextFieldBlur(event, name)}
				onKeyPress={event => this.onTextFieldKeyPress(event, name)}
				id={name}
				placeholder={placeholder}
				{...(type ? { type } : {})}
				{...(autosize ? { autosize } : {})}
				margin="normal"
				helperText={this.state.errors[name]}
				error={!!this.state.errors[name]}
				disabled={disabled}
				maxLength={maxLength}
				inputRef={this[name]}
			/>
		);
	}

	onTextFieldBlur(event, name) {
		let newState = _.cloneDeep(this.state);
		newState.touched[name] = true;
		this.setState(newState, this.validateAll);
	}	

	onTextFieldKeyPress(event, name) {
		if (!this.isSubmitEnabled()) return;
		if (event.key === 'Enter') {
			this.submitForm();
		}
	}

	onTextFieldChange(event, name) {
		this.setFieldState(name, event.target.value);
	}

	setFieldState(fieldName, value) {
		let newState = _.cloneDeep(this.state);
		newState.touched[fieldName] = true;
		newState[fieldName] = value;
		this.setState(newState, this.validateAll);
	}

	getFormErrors(includeAllField) {
		// includeAllField - include all fields in validation, not only touched
		let validateFields = null;

		if (this.state.currentStep === SCREEN_TYPE.MLS_API) {
			validateFields = ['mlsOfficeId'];
		}

		let foundErrors = {};
		for (let field in ValidationSchema) {
			if (validateFields.includes(field)) {
				let validate = ValidationSchema[field];
				if (validate) {
					let error = validate(this.state[field], this.state, includeAllField);
					if (error) {
						foundErrors[field] = error;
					}
				}
			}
		}
		return foundErrors;
	}

	validateAll() {
		this.setState({ errors: this.getFormErrors(false) });
	}
}

InviteUserModal.propTypes = {
	companies: PropTypes.object,
	mls: PropTypes.object,
	modalShown: PropTypes.bool,
	hideModal: PropTypes.func,
	resetModal: PropTypes.func,
	classes: PropTypes.object,
	email: PropTypes.string,
	showApiError: PropTypes.bool,
	company: PropTypes.object,
	user: PropTypes.object.isRequired,
	showErrorPopup: PropTypes.func.isRequired,
	selectedCompanyId: PropTypes.string,
	addOfficeId: PropTypes.func.isRequired,
	clearConnectMlsState: PropTypes.func.isRequired,
	sendMlsReviewRequest: PropTypes.func.isRequired,
	mlsName: PropTypes.string.isRequired,
	mlsConnectionId: PropTypes.string.isRequired,
	onClose: PropTypes.func.isRequired,
	sendVerifyAccountEmail: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
	company: state.companies.rawDataById ? state.companies.rawDataById[state.companies.selectedId] : null,
	modalShown: state.modal.modalShown,
	companies: state.companies,
	mls: state.mls,
	user: state.session.user,
	selectedCompanyId: state.companies.selectedId,
});

const mapDispatchToProps = dispatch => bindActionCreators({
	hideModal,
	resetModal,
	showErrorPopup,
	addOfficeId,
	sendMlsReviewRequest,
	clearConnectMlsState,
	sendVerifyAccountEmail
}, dispatch);

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(InviteUserModal);


// WEBPACK FOOTER //
// ./src/components/Modal/AddMlsOfficeModal.js